import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Session, SessionPub } from '../../api/types';
import { SessionService } from '../../services/sessionService';
import { slugify } from '../../utils/urlUtils';
import { formatScanTime } from '../../utils/dateUtils';
import { useElapsedTime } from '../../hooks/useElapsedTime';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import RulesSheet from '../../components/RulesSheet';
import SubmitSheet from '../../components/SubmitSheet';
import PlayerNameInput from '../../components/PlayerNameInput';
import Map from '../../components/Map';
import PubRow from '../../components/PubRow';
import CopyRow from '../../components/ui/CopyRow';
import { RulesIcon, ChevronDownIcon } from '../../components/ui/icons';
import './MyCrawl.css';

interface MyCrawlProps {
  session?: Session | null;
  /** Pubs scanned on this device but not yet confirmed by the server */
  pendingPubIds?: string[];
}

const NO_PENDING: string[] = [];

/** Sheet stays mounted through its exit transition and restores focus to the
 *  Rules chip on unmount — jumping to the share row has to wait that out or
 *  the focus restore scrolls us straight back up. */
const SHEET_EXIT_MS = 340;

/**
 * The main crawl screen: hero with the pump-clip clock, the pub list with
 * directions on every row, the map tab, submit, and the share/resume
 * disclosure.
 */
const MyCrawl: React.FC<MyCrawlProps> = ({ session: propSession, pendingPubIds = NO_PENDING }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [activeTab, setActiveTab] = useState<'pubs' | 'map'>('pubs');
  const [shareOpen, setShareOpen] = useState(false);
  /** Bumped to request a jump to the share panel; the effect below serves it. */
  const [jumpSeq, setJumpSeq] = useState(0);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  const navigate = useNavigate();
  const { hash } = useLocation();
  const elapsedTime = useElapsedTime(session);
  const { copied, copy } = useCopyToClipboard();
  const discloseRef = useRef<HTMLButtonElement>(null);
  const shareBodyRef = useRef<HTMLDivElement>(null);
  const jumpTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Which jump request has been served. A ref, not state — acknowledging it
   *  must not re-run the effect that does the scrolling. */
  const servedJump = useRef(0);

  useEffect(() => () => clearTimeout(jumpTimer.current), []);

  // Update session when prop changes (from parent component)
  useEffect(() => {
    if (propSession) {
      setSession(propSession);
    }
  }, [propSession]);

  const initialise = useCallback(async () => {
    // Only initialise if no session prop is provided
    if (propSession) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const sessionId = SessionService.getSessionId();

      if (sessionId) {
        try {
          const sessionData = await SessionService.getSession(sessionId);
          setSession(sessionData);
          setError(null);
        } catch (sessionError) {
          console.error('Error fetching session:', sessionError);
          if (sessionError instanceof Error && sessionError.message.includes('410')) {
            // Session expired, redirect to home to trigger popup
            window.location.href = '/';
            return;
          }
          setError('Your crawl could not be loaded. Check your signal, then try again.');
        }
      } else {
        setError('No crawl found on this device. Scan a pub QR code to start one.');
      }
    } catch (err) {
      console.error('Error initialising crawl screen:', err);
      setError('Your crawl could not be loaded. Check your signal, then try again.');
    } finally {
      setLoading(false);
    }
  }, [propSession]);

  useEffect(() => {
    initialise();
  }, [initialise]);

  const isFinalised = session?.isFinalised || session?.isComplete || false;
  const pubs = session?.pubs ?? [];
  const completedPubs = pubs.filter(pub => pub.isComplete).length;
  const totalPubs = pubs.length;
  const remaining = totalPubs - completedPubs;
  const syncing = pubs.filter(pub => !pub.isComplete && pendingPubIds.includes(pub.id)).length;
  const resumeLink = session ? `${window.location.origin}/?session=${session.id}` : '';
  const canShare = !!session?.resumeCode && !isFinalised;

  /** Rule 5 in the sheet links here: close the sheet, expand the disclosure and
   *  queue the jump — delayed past the sheet's exit so its focus restore doesn't
   *  scroll us straight back up. */
  const handleShowShare = () => {
    setShareOpen(true);
    setShowRules(false);
    clearTimeout(jumpTimer.current);
    jumpTimer.current = setTimeout(() => setJumpSeq(seq => seq + 1), SHEET_EXIT_MS);
  };

  // /#share (the About page's "share code or link") lands here. The session
  // arrives after mount, so wait for canShare rather than firing on the hash
  // alone — the disclosure doesn't exist until then.
  useEffect(() => {
    if (hash !== '#share' || !canShare) return;
    setShareOpen(true);
    setJumpSeq(seq => seq + 1);
  }, [hash, canShare]);

  // Scrolls only once the panel is in the DOM — a jump fired on a timer races the
  // render, lands on the collapsed row and leaves the panel below the fold. If the
  // request arrives first it latches here until `shareOpen` renders the panel.
  // `block: 'end'` brings the whole panel into view rather than centring the row.
  useEffect(() => {
    const panel = shareBodyRef.current;
    if (jumpSeq === servedJump.current || !panel) return;
    servedJump.current = jumpSeq;
    panel.scrollIntoView({ behavior: 'smooth', block: 'end' });
    discloseRef.current?.focus({ preventScroll: true });
  }, [jumpSeq, shareOpen]);

  const handleNameChange = async (newName: string): Promise<void> => {
    if (!session) {
      throw new Error('No active session');
    }
    const updatedSession = await SessionService.updateSession(session.id, {
      contenderName: newName,
    });
    setSession(updatedSession);
  };

  const handleFinishConfirm = async (name: string) => {
    if (!session) return;
    const finished = await SessionService.finishSession(session.id, name);
    setSession(finished);
    setShowFinishConfirm(false);
    if (finished.crawlName) {
      navigate(`/crawls/${slugify(finished.crawlName)}`);
    }
  };

  const lastScan = pubs
    .filter((pub): pub is SessionPub & { completionTime: string } => !!pub.completionTime)
    .sort((a, b) => new Date(b.completionTime).getTime() - new Date(a.completionTime).getTime())[0];

  if (loading) {
    return (
      <div className="mycrawl" data-testid="my-crawl">
        <div className="mycrawl-skeleton">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="mycrawl-skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !propSession) {
    return (
      <div className="mycrawl" data-testid="my-crawl">
        <div className="mycrawl-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="mycrawl-secondary">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mycrawl" data-testid="my-crawl">
      {/* Hero: crawl name + pump clip */}
      <div className="mycrawl-hero">
        <h1 className="mycrawl-name">{session?.crawlName || 'scan-drink-race.com'}</h1>
        <div className="pump-clip">
          <div className="pump-clip-label">{isFinalised ? 'Final time' : 'Elapsed'}</div>
          <div className="pump-clip-clock tabular">{elapsedTime || '0:00:00'}</div>
          <div className="pump-clip-sub">
            {completedPubs} of {totalPubs} pubs
            {!isFinalised && remaining > 0 && ` · ${remaining} to go`}
            {!isFinalised && syncing > 0 && ` · ${syncing} syncing`}
            {isFinalised && ' · submitted'}
          </div>
        </div>
        <div className="mycrawl-chips">
          <div className="mycrawl-name-chip">
            <PlayerNameInput
              key={session?.contenderName || 'empty'}
              currentName={session?.contenderName || ''}
              onNameChange={handleNameChange}
              disabled={isFinalised}
            />
          </div>
          <button className="mycrawl-chip-ghost" onClick={() => setShowRules(true)}>
            <RulesIcon /> Rules
          </button>
        </div>
      </div>

      {/* Pubs / Map segmented control */}
      <div className="mycrawl-segmented" role="tablist" aria-label="Crawl views">
        <button
          role="tab"
          aria-selected={activeTab === 'pubs'}
          onClick={() => setActiveTab('pubs')}
        >
          The pubs
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'map'}
          onClick={() => setActiveTab('map')}
        >
          Map
        </button>
      </div>

      {activeTab === 'pubs' ? (
        <ul className="mycrawl-list">
          {pubs.map((pub, index) => (
            <PubRow key={pub.id} pub={pub} index={index} pending={pendingPubIds.includes(pub.id)} />
          ))}
        </ul>
      ) : (
        <div className="mycrawl-map">
          <Map pubs={pubs} pendingPubIds={pendingPubIds} />
        </div>
      )}

      <div className="mycrawl-actions">
        {!isFinalised && (
          <button
            onClick={() => setShowFinishConfirm(true)}
            className="mycrawl-primary"
            data-testid="submit-crawl"
          >
            Submit my crawl
          </button>
        )}

        {isFinalised && session?.crawlName && (
          <button
            onClick={() => navigate(`/crawls/${slugify(session.crawlName!)}`)}
            className="mycrawl-secondary"
          >
            View leaderboard
          </button>
        )}

        {canShare && (
          <>
            <button
              ref={discloseRef}
              className="mycrawl-disclose"
              aria-expanded={shareOpen}
              onClick={() => setShareOpen(open => !open)}
              data-testid="share-disclose"
            >
              <span>Share or resume this crawl</span>
              <ChevronDownIcon className="mycrawl-disclose-chev" />
            </button>
            {shareOpen && (
              <div className="mycrawl-share-body" ref={shareBodyRef}>
                <div className="mycrawl-share-label">Share code</div>
                <CopyRow
                  value={session.resumeCode}
                  variant="code"
                  copied={copied === 'code'}
                  onCopy={() => copy(session.resumeCode!, 'code')}
                  testId="resume-code"
                />
                <div className="mycrawl-share-label mycrawl-share-label-gap">Share link</div>
                <CopyRow
                  value={resumeLink}
                  display={resumeLink.replace(/^https?:\/\//, '')}
                  variant="link"
                  copied={copied === 'link'}
                  onCopy={() => copy(resumeLink, 'link')}
                />
                <p className="mycrawl-share-note">
                  Enter the code at <b>scan-drink-race.com</b> — or open the link — to carry on
                  from another device, or to get your team scanning onto this crawl.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <RulesSheet
        isOpen={showRules}
        onClose={() => setShowRules(false)}
        onShowShare={canShare ? handleShowShare : undefined}
      />

      <SubmitSheet
        isOpen={showFinishConfirm}
        onClose={() => setShowFinishConfirm(false)}
        completedPubs={completedPubs}
        totalPubs={totalPubs}
        elapsedTime={elapsedTime || '0:00:00'}
        lastScan={lastScan ? { pubName: lastScan.name, time: formatScanTime(lastScan.completionTime) } : null}
        defaultName={session?.contenderName || ''}
        onConfirm={handleFinishConfirm}
      />
    </div>
  );
};

export default MyCrawl;
